import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { addReply, findTicket, threadFor } from "./tickets";
import { TicketStatus } from "./PortalTickets";

export function PortalTicket() {
  const { brand, brandId, user } = usePortal();
  const { id } = useParams({ strict: false }) as { id: string };
  // Brand-scoped: a detail URL must not read another brand's ticket.
  const ticket = findTicket(id, brandId);
  const [reply, setReply] = useState("");
  const [, bump] = useState(0); // re-render after a reply is written to the store

  // Read the thread from the store each render, so a reply persists across
  // leaving and reopening the ticket (a fake send must not silently discard it).
  const thread = ticket ? threadFor(ticket) : [];

  function send() {
    const body = reply.trim();
    if (!body || !ticket) return;
    addReply(ticket.id, body);
    setReply("");
    bump((n) => n + 1);
  }

  if (!ticket) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "60px 28px", textAlign: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#1d241f" }}>We couldn't find that ticket</div>
        <Link to="/portal/tickets" style={{ color: brand.ink, fontSize: 13.5 }}>Back to my tickets</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "26px 28px 48px", display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#8a8e88" }}>
        <Link to="/portal/tickets" style={{ color: "#8a8e88", textDecoration: "none" }}>My tickets</Link>
        <Icon name="chevron-right" size={13} color="#bcbfba" />
        <span style={{ fontFamily: "'Space Mono', monospace" }}>{ticket.id}</span>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <TicketStatus status={ticket.status} tone={ticket.statusTone} />
          <span style={{ fontSize: 12, color: "#8a8e88" }}>Opened {ticket.opened} · with {ticket.owner}</span>
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: "-0.5px", color: "#1d241f", textWrap: "pretty" }}>{ticket.subject}</h1>
      </div>

      {/* thread */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {thread.length === 0 ? (
          <div style={{ border: "1px dashed #dcdad2", background: "#fafaf7", borderRadius: 3, padding: "26px 20px", textAlign: "center", color: "#8a8e88", fontSize: 13.5 }}>
            No replies yet — we'll respond here. You'll get an email when we do.
          </div>
        ) : (
          thread.map(([who, msg, when], i) => {
            const you = who === "you";
            return (
              <div key={i} style={{ display: "flex", gap: 11, flexDirection: you ? "row-reverse" : "row" }}>
                <span style={{ flex: "none", width: 30, height: 30, borderRadius: 3, background: you ? "#f0efe9" : brand.soft, color: you ? "#4a504a" : brand.ink, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                  {you ? user.initials : "TR"}
                </span>
                <div style={{ maxWidth: "78%", background: you ? "#fff" : brand.soft, borderRadius: 3, boxShadow: you ? "inset 0 0 0 1px rgba(0,0,0,.08)" : `inset 0 0 0 1px ${brand.line}`, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 5 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1d241f" }}>{you ? "You" : ticket.owner}</span>
                    <span style={{ fontSize: 11, color: "#8a8e88" }}>{when}</span>
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#2d332e", textWrap: "pretty" }}>{msg}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* reply composer */}
      <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)", padding: "12px 14px" }}>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Add a reply…"
          rows={3}
          style={{ width: "100%", border: 0, outline: 0, resize: "vertical", fontSize: 14, lineHeight: 1.6, color: "#1d241f", fontFamily: "inherit" }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid #f4f3ee", paddingTop: 10, marginTop: 6 }}>
          <button style={{ border: "1px dashed #dcdad2", background: "#fff", borderRadius: 2, padding: "7px 12px", fontSize: 12.5, fontWeight: 600, color: "#4a504a", display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
            <Icon name="paperclip" size={14} color="#8a8e88" />
            Attach
          </button>
          <span style={{ flex: 1 }} />
          <button
            disabled={!reply.trim()}
            onClick={send}
            style={{ border: 0, background: reply.trim() ? brand.ink : "#f0efe9", color: reply.trim() ? "#fff" : "#8a8e88", borderRadius: 3, padding: "9px 18px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", cursor: reply.trim() ? "pointer" : "not-allowed" }}
          >
            Send reply
          </button>
        </div>
      </div>
    </div>
  );
}
